import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { leaveApi } from "../../../api/leaveApi";
import {
  useLeavePolicies,
  useCreateLeavePolicy,
  useUpdateLeavePolicy,
  useDeleteLeavePolicy,
} from "../../../api/queries/leaveQueries";
import { getApiErrorMessage } from "../../../utils/apiError";
import Layout from "../../../components/layout/Layout";
import { toast } from "react-toastify";
import "./LeaveConfigurationPage.css";
import { useCanModify } from "../../../hooks/usePermissions";

const LeaveConfigurationScreen = () => {
  // View-only roles see the list without create / edit / delete
  const canModify = useCanModify("leave", { company: true });
  const { data: policies = [] } = useLeavePolicies();
  const createPolicy = useCreateLeavePolicy();
  const updatePolicy = useUpdateLeavePolicy();
  const deletePolicy = useDeleteLeavePolicy();
  const isSaving = createPolicy.isPending || updatePolicy.isPending;
  const [editingId, setEditingId] = useState(null);

  const initialFormState = {
    name: "",
    code: "",
    description: "",
    leaveTypeId: "",
    type: "paid",
    isActive: true,
    annualEntitlement: "",
    maxCarryOverDays: 0,
    requiresApproval: false,
    minDaysNotice: "",
    validFrom: "",
    validUntil: "",
    resetEveryYear: false,
    criteria: {},
    minYearsOfService: "", // edited here, sent inside `criteria`
  };

  const [formData, setFormData] = useState(initialFormState);

  const { data: leaveTypes = [] } = useQuery({
    queryKey: ["leaveTypes"],
    queryFn: leaveApi.getLeaveTypes,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  /**
   * Only the policy's own fields are sent (the API rejects unknown ones and a
   * PATCH changes only what's sent). Eligibility lives in `criteria`; other
   * criteria keys are kept as they are.
   */
  const buildPayload = () => {
    const { minYearsOfService: _drop, ...otherCriteria } =
      formData.criteria || {};
    const minYears =
      formData.minYearsOfService === ""
        ? null
        : Number(formData.minYearsOfService);
    return {
      name: formData.name.trim(),
      code: formData.code.trim(),
      description: formData.description || null,
      leaveTypeId: formData.leaveTypeId,
      type: formData.type,
      isActive: !!formData.isActive,
      annualEntitlement: parseFloat(formData.annualEntitlement) || 0,
      maxCarryOverDays: parseInt(formData.maxCarryOverDays) || 0,
      requiresApproval: !!formData.requiresApproval,
      minDaysNotice:
        formData.minDaysNotice === "" || formData.minDaysNotice === null
          ? null
          : parseInt(formData.minDaysNotice),
      validFrom: formData.validFrom || null,
      validUntil: formData.validUntil || null,
      resetEveryYear: !!formData.resetEveryYear,
      criteria:
        minYears === null
          ? otherCriteria
          : { ...otherCriteria, minYearsOfService: minYears },
    };
  };

  const handleSave = async () => {
    const minYears = formData.minYearsOfService;
    if (
      minYears !== "" &&
      (!Number.isInteger(Number(minYears)) || Number(minYears) < 0)
    ) {
      toast.error(
        "Minimum years of service must be a whole number of 0 or more.",
      );
      return;
    }
    if (
      formData.validFrom &&
      formData.validUntil &&
      formData.validFrom > formData.validUntil
    ) {
      toast.error("Valid From must be on or before Valid Until.");
      return;
    }
    const payload = buildPayload();

    try {
      if (editingId) {
        await updatePolicy.mutateAsync({ id: editingId, data: payload });
        toast.success("Policy updated successfully!");
      } else {
        await createPolicy.mutateAsync(payload);
        toast.success("Policy created successfully!");
      }
      setEditingId(null);
      setFormData(initialFormState);
    } catch (err) {
      // 409 = another active policy on this leave type; 400 = validation (may be a list)
      toast.error(getApiErrorMessage(err, "Failed to save policy"));
    }
  };

  // Load only the editable fields (not id, leaveType object, timestamps…)
  const handleEdit = (policy) => {
    setEditingId(policy.id);
    setFormData({
      ...initialFormState,
      name: policy.name || "",
      code: policy.code || "",
      description: policy.description || "",
      leaveTypeId: policy.leaveTypeId || policy.leaveType?.id || "",
      type: policy.type || "paid",
      isActive: policy.isActive ?? true,
      annualEntitlement: policy.annualEntitlement ?? "",
      maxCarryOverDays: policy.maxCarryOverDays ?? 0,
      requiresApproval: !!policy.requiresApproval,
      minDaysNotice: policy.minDaysNotice ?? "",
      validFrom: policy.validFrom ? String(policy.validFrom).slice(0, 10) : "",
      validUntil: policy.validUntil
        ? String(policy.validUntil).slice(0, 10)
        : "",
      resetEveryYear: !!policy.resetEveryYear,
      criteria: policy.criteria || {},
      minYearsOfService: policy.criteria?.minYearsOfService ?? "",
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this policy?")) {
      try {
        await deletePolicy.mutateAsync(id);
        toast.success("Policy deleted successfully!");
      } catch (err) {
        toast.error(getApiErrorMessage(err, "Failed to delete policy"));
      }
    }
  };

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4 leave-config">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <h1 className="fw-bold text-dark m-0 page-title">Leave Policies</h1>
            <p className="text-muted small mb-0">
              {editingId
                ? `Editing the ${formData.name || "selected"} policy.`
                : "Set up a policy below; saved policies are listed underneath."}
            </p>
          </div>
          <div>
            {editingId && (
              <button
                className="btn btn-outline-secondary me-2 btn-action"
                onClick={() => {
                  setEditingId(null);
                  setFormData(initialFormState);
                }}
              >
                Cancel
              </button>
            )}
            {canModify && (
              <button
                className="btn btn-dark px-4 btn-action"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : editingId ? "Update" : "Save"}
              </button>
            )}
          </div>
        </div>

        <div className="config-card p-4 mb-5">
          <div className="row g-4">
            <div className="col-md-4">
              <div className="section-title">General Info</div>
              <div className="mb-3">
                <label className="form-label">Name</label>
                <input
                  name="name"
                  className="form-control"
                  onChange={handleChange}
                  value={formData.name}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Code</label>
                <input
                  name="code"
                  className="form-control"
                  onChange={handleChange}
                  value={formData.code}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Base Leave Type</label>
                <select
                  name="leaveTypeId"
                  className="form-select"
                  onChange={handleChange}
                  value={formData.leaveTypeId}
                >
                  <option value="">-- Select --</option>
                  {leaveTypes.map((lt) => (
                    <option key={lt.id} value={lt.id}>
                      {lt.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-md-4">
              <div className="section-title">Entitlement</div>
              <div className="mb-3">
                <label className="form-label">Annual Entitlement</label>
                <input
                  type="number"
                  name="annualEntitlement"
                  className="form-control"
                  onChange={handleChange}
                  value={formData.annualEntitlement}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Max Carry Over</label>
                <input
                  type="number"
                  name="maxCarryOverDays"
                  className="form-control"
                  onChange={handleChange}
                  value={formData.maxCarryOverDays}
                />
              </div>
              <div className="mb-3">
                <label className="form-label" htmlFor="policy-min-years">
                  Min. Years of Service
                </label>
                <input
                  id="policy-min-years"
                  type="number"
                  min="0"
                  step="1"
                  name="minYearsOfService"
                  className="form-control"
                  onChange={handleChange}
                  value={formData.minYearsOfService}
                  placeholder="e.g. 1"
                />
                <div className="form-text">
                  Eligible from this hiring anniversary; leave renews every
                  anniversary after. Empty = from hiring.
                </div>
              </div>
              <div className="form-check form-switch mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="resetEveryYear"
                  checked={formData.resetEveryYear}
                  onChange={handleChange}
                />
                <label className="form-check-label small fw-bold">
                  Reset Every Year
                </label>
              </div>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="requiresApproval"
                  checked={formData.requiresApproval}
                  onChange={handleChange}
                />
                <label className="form-check-label small fw-bold">
                  Requires Approval
                </label>
              </div>
            </div>

            <div className="col-md-4">
              <div className="section-title">Validity</div>
              <div className="mb-3">
                <label className="form-label">Valid From</label>
                <input
                  type="date"
                  name="validFrom"
                  className="form-control"
                  onChange={handleChange}
                  value={formData.validFrom}
                />
              </div>
              <div className="mb-3">
                <label className="form-label" htmlFor="policy-valid-until">
                  Valid Until{" "}
                  <span className="text-slate-400 fw-normal">(optional)</span>
                </label>
                <input
                  id="policy-valid-until"
                  type="date"
                  name="validUntil"
                  className="form-control"
                  onChange={handleChange}
                  value={formData.validUntil}
                />
                <div className="fs-070 text-slate-500 mt-1">
                  Empty means no end date. A leave type can have only one active
                  policy for any period, so end or deactivate the older one
                  first.
                </div>
              </div>
              <div className="form-check form-switch">
                <input
                  id="policy-active"
                  className="form-check-input"
                  type="checkbox"
                  name="isActive"
                  checked={!!formData.isActive}
                  onChange={handleChange}
                />
                <label
                  className="form-check-label small fw-bold"
                  htmlFor="policy-active"
                >
                  Active
                </label>
              </div>
            </div>
          </div>
        </div>

        <h2 className="fs-6 fw-bold text-navy mt-4 mb-2">Saved policies</h2>

        <div className="glass-table-card">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="custom-table-header">
                <tr>
                  <th className="py-3 ps-4 border-0">Policy Name</th>
                  <th className="py-3 border-0">Code</th>
                  <th className="py-3 border-0">Leave Type</th>
                  <th className="py-3 border-0">Type</th>
                  <th className="py-3 border-0">Entitlement</th>
                  <th className="py-3 border-0">Eligible after</th>
                  <th className="py-3 border-0">Status</th>
                  <th className="py-3 border-0">Valid From</th>
                  <th className="py-3 border-0">Valid Until</th>

                  <th className="py-3 pe-4 border-0 text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {policies.map((p) => (
                  <tr key={p.id}>
                    <td className="fw-bold text-dark ps-4">{p.name}</td>
                    <td className="text-muted">{p.code}</td>
                    <td className="text-muted">{p.leaveType?.name || "—"}</td>
                    <td className="text-muted">{p.type}</td>
                    <td>
                      <span className="badge bg-light text-dark border border-secondary-subtle fs-070">
                        {p.annualEntitlement} Days
                      </span>
                    </td>
                    <td className="text-muted">
                      {p.criteria?.minYearsOfService
                        ? `${p.criteria.minYearsOfService} yr`
                        : "Hiring"}
                    </td>
                    <td>
                      <span
                        className={`status-pill status-pill--${p.isActive === false ? "gray" : "green"}`}
                      >
                        {p.isActive === false ? "Inactive" : "Active"}
                      </span>
                    </td>
                    <td className="text-muted">
                      {p.validFrom ? p.validFrom : "N/A"}
                    </td>
                    <td className="text-muted">
                      {p.validUntil ? p.validUntil : "No end date"}
                    </td>

                    <td className="text-end pe-4">
                      {canModify ? (
                        <>
                          <button
                            className="btn btn-sm btn-outline-primary btn-action me-2"
                            onClick={() => handleEdit(p)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger btn-action"
                            onClick={() => handleDelete(p.id)}
                          >
                            Delete
                          </button>
                        </>
                      ) : (
                        <span className="fs-070 text-slate-400">View only</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default LeaveConfigurationScreen;
